import {
  gql,
  useMutation,
  ApolloQueryResult,
  MutationFunctionOptions,
  FetchResult,
  useQuery,
} from '@apollo/client';
import React, {useMemo} from 'react';
import {useSnackbar} from 'notistack';
import {Box, Typography} from '@mui/material';
import {
  CONTACT_BASIC_FRAGMENT,
  NOTE_DETAILS_FRAGMENT,
  RiskProfileFragment,
  RiskSectionFragment,
} from '../../graphql/fragments';
import {
  UpsertRiskProfile,
  UpsertRiskProfileVariables,
} from '../../schema-types/UpsertRiskProfile';
import {
  UpsertRiskSection,
  UpsertRiskSectionVariables,
} from '../../schema-types/UpsertRiskSection';
import {useReviewContext} from '../../contexts/ReviewContext';
import {GetRisk, GetRiskVariables} from '../../schema-types/GetRisk';
import {useFeaturePermission} from '../../contexts/FeatureBoundary';
import {GlobalPermissionEnum, RiskType} from '../../schema-types/globalTypes';
import {UpdateContact, UpdateContactVariables} from '../../schema-types/UpdateContact';
import {UPDATE_CONTACT} from '../../components/AccountOverview/accountOverviewQueries';
import {
  ResyncCuroAtrForRiskProfile,
  ResyncCuroAtrForRiskProfileVariables,
} from '../../schema-types/ResyncCuroAtrForRiskProfile';
import Loading from '../../components/Loading/Loading';
import {REVIEW_VALIDATION} from '../../pages/HandoverSummaryPage/useReviewValidation';
import validateUUID from '../../utils/validateUUID';

export const GET_RISK = gql`
  query GetRisk($reviewId: UUID!) {
    reviewDetails(valueId: $reviewId, keyId: "review_id") {
      id
      riskProfiles {
        ...RiskProfileFragment
      }
      contacts {
        ...ContactBasicFragment
        giaHasLiquidityNeeds
        pensionHasLiquidityNeeds
        accountContacts {
          id
          account {
            id
            name
          }
        }
      }
    }
  }
  ${RiskProfileFragment}
  ${CONTACT_BASIC_FRAGMENT}
`;

export const GET_RISK_AND_NOTES = gql`
  query GetRiskAndNotes($reviewId: UUID!, $accountId: UUID!, $contactId: UUID) {
    reviewDetails(valueId: $reviewId, keyId: "review_id") {
      id
      ...HandoverNotesReviewDetailsFragment
      contacts {
        ...ContactBasicFragment
        giaHasLiquidityNeeds
        pensionHasLiquidityNeeds
        accountContacts {
          id
          account {
            id
            name
          }
        }
      }
      riskProfiles {
        ...RiskProfileFragment
      }
    }
    previousNotes(
      keyId: "account_id"
      valueId: $accountId
      contactKeyId: "contact_id"
      contactValueId: $contactId
    ) {
      ...HandoverNotesReviewDetailsFragment
    }
  }
  ${RiskProfileFragment}
  ${NOTE_DETAILS_FRAGMENT}
  ${CONTACT_BASIC_FRAGMENT}
`;

export const UPSERT_RISK_PROFILE = gql`
  mutation UpsertRiskProfile($inputs: [RiskProfileInput!]!) {
    upsertRiskProfiles(inputs: $inputs) {
      errors
      ok
      riskProfiles {
        ...RiskProfileFragment
      }
    }
  }
  ${RiskProfileFragment}
`;

export const UPSERT_RISK_SECTION = gql`
  mutation UpsertRiskSection($inputs: [RiskProfileSectionInput!]!) {
    upsertRiskProfileSections(inputs: $inputs) {
      errors
      ok
      riskProfileSections {
        ...RiskSectionFragment
      }
    }
  }
  ${RiskSectionFragment}
`;

export const RESYNC_CURO_ATR = gql`
  mutation ResyncCuroAtrForRiskProfile($reviewId: UUID!, $contactId: UUID!) {
    resyncCuroAtrForRiskProfile(reviewId: $reviewId, contactId: $contactId) {
      ok
      errors
    }
  }
`;

export type RiskContextType = {
  upsertRiskProfile: (
    options?: MutationFunctionOptions<UpsertRiskProfile, UpsertRiskProfileVariables>
  ) => Promise<FetchResult<UpsertRiskProfile>>;
  upsertRiskProfileLoading: boolean;
  upsertRiskProfileError: Error | undefined;
  upsertRiskSection: (
    options?: MutationFunctionOptions<UpsertRiskSection, UpsertRiskSectionVariables>
  ) => Promise<FetchResult<UpsertRiskSection>>;
  upsertRiskSectionLoading: boolean;
  upsertRiskSectionError: Error | undefined;
  riskData: GetRisk | undefined;
  riskLoading: boolean;
  riskError: Error | undefined;
  refetchRiskData: () => Promise<ApolloQueryResult<GetRisk>>;
  multiscoreEnabled: boolean;
  saveDummyRiskProfile: (showSuccess: boolean) => Promise<void>;
  resyncCuroAtrForRiskProfileMutation: (
    options?: MutationFunctionOptions<
      ResyncCuroAtrForRiskProfile,
      ResyncCuroAtrForRiskProfileVariables
    >
  ) => Promise<FetchResult<ResyncCuroAtrForRiskProfile>>;
  resyncCuroAtrForRiskProfileLoading: boolean;
  resyncCuroAtrForRiskProfileError: Error | undefined;
};

export const RiskContext = React.createContext<RiskContextType>({
  upsertRiskProfile: () => Promise.resolve({} as FetchResult<UpsertRiskProfile>),
  upsertRiskProfileLoading: false,
  upsertRiskProfileError: undefined,
  upsertRiskSection: () => Promise.resolve({} as FetchResult<UpsertRiskSection>),
  upsertRiskSectionLoading: false,
  upsertRiskSectionError: undefined,
  riskData: undefined,
  riskLoading: false,
  riskError: undefined,
  refetchRiskData: () => Promise.resolve({} as ApolloQueryResult<GetRisk>),
  multiscoreEnabled: false,
  saveDummyRiskProfile: () => Promise.resolve(),
  resyncCuroAtrForRiskProfileMutation: () =>
    Promise.resolve({} as ApolloQueryResult<ResyncCuroAtrForRiskProfile>),
  resyncCuroAtrForRiskProfileLoading: false,
  resyncCuroAtrForRiskProfileError: undefined,
});

// Custom hook that contains all the risk provider logic
// DO NOT IMPORT DIRECTLY
const useRiskProvider = (): RiskContextType => {
  const {reviewId, reviewDetails} = useReviewContext();
  const {enqueueSnackbar} = useSnackbar();
  const isAdmin = useFeaturePermission([GlobalPermissionEnum.can_access_admin_features]);
  const [
    upsertRiskSectionMutation,
    {loading: upsertRiskSectionLoading, error: upsertRiskSectionError},
  ] = useMutation<UpsertRiskSection, UpsertRiskSectionVariables>(UPSERT_RISK_SECTION, {
    refetchQueries: [
      {
        query: REVIEW_VALIDATION,
        variables: {
          valueId: reviewId!,
        },
      },
    ],
    awaitRefetchQueries: true,
  });
  const [
    upsertRiskProfileMutation,
    {loading: upsertRiskProfileLoading, error: upsertRiskProfileError},
  ] = useMutation<UpsertRiskProfile, UpsertRiskProfileVariables>(UPSERT_RISK_PROFILE, {
    refetchQueries: [
      {
        query: REVIEW_VALIDATION,
        variables: {
          valueId: reviewId!,
        },
      },
    ],
    awaitRefetchQueries: true,
  });
  const [
    resyncCuroAtrForRiskProfileMutation,
    {
      loading: resyncCuroAtrForRiskProfileLoading,
      error: resyncCuroAtrForRiskProfileError,
    },
  ] = useMutation<ResyncCuroAtrForRiskProfile, ResyncCuroAtrForRiskProfileVariables>(
    RESYNC_CURO_ATR
  );
  const [updateContactMutation] = useMutation<UpdateContact, UpdateContactVariables>(
    UPDATE_CONTACT
  );

  const upsertRiskProfile = async (
    options?: MutationFunctionOptions<UpsertRiskProfile, UpsertRiskProfileVariables>
  ) => {
    try {
      const result = await upsertRiskProfileMutation({...options});
      if (result.errors?.length) {
        enqueueSnackbar(
          `Error updating risk profile: ${result.errors
            .map((e) => e.message)
            .join(', ')}`,
          {
            variant: 'error',
            persist: true,
          }
        );
      }
      if (result.data?.upsertRiskProfiles?.errors?.length) {
        enqueueSnackbar(
          `Error updating risk profile: ${result.data.upsertRiskProfiles.errors.join(
            ', '
          )}`,
          {
            variant: 'error',
            persist: true,
          }
        );
      }
      return result;
    } catch (error) {
      enqueueSnackbar(
        `Error updating risk profile: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`,
        {
          variant: 'error',
          persist: true,
        }
      );
      throw error;
    }
  };

  const upsertRiskSection = async (
    options?: MutationFunctionOptions<UpsertRiskSection, UpsertRiskSectionVariables>
  ) => {
    const sectionName = options?.variables?.inputs?.[0]?.riskType
      ? `"${options?.variables?.inputs?.[0]?.riskType}`
      : '';
    try {
      const result = await upsertRiskSectionMutation({...options});
      if (result.errors?.length) {
        enqueueSnackbar(
          `Error updating risk section ${sectionName}: ${result.errors
            .map((e) => e.message)
            .join(', ')}`,
          {
            variant: 'error',
            persist: true,
          }
        );
      }
      if (result.data?.upsertRiskProfileSections?.errors?.length) {
        enqueueSnackbar(
          `Error updating risk section ${sectionName}: ${result.data.upsertRiskProfileSections.errors.join(
            ', '
          )}`,
          {
            variant: 'error',
            persist: true,
          }
        );
      }
      return result;
    } catch (error) {
      enqueueSnackbar(
        `Error updating risk section ${sectionName}: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`,
        {
          variant: 'error',
          persist: true,
        }
      );
      throw error;
    }
  };

  const variables: GetRiskVariables = useMemo(
    () => ({
      reviewId: validateUUID(reviewId)!,
    }),
    [reviewId]
  );
  const {
    data: riskData,
    loading: riskLoading,
    error: riskError,
    refetch: _refetchRiskData,
  } = useQuery<GetRisk, GetRiskVariables>(GET_RISK, {
    variables,
    fetchPolicy: 'network-only',
    skip: !reviewId,
  });

  const refetchRiskData = () => {
    debugger;
    return _refetchRiskData();
  };

  // multiscoreEnabled is true if the review has more than one contact (joint review)
  const multiscoreEnabled = useMemo(() => {
    const contacts = riskData?.reviewDetails?.contacts || [];
    return contacts.length > 1;
  }, [riskData?.reviewDetails?.contacts]);

  const saveDummyRiskProfile = async (showSuccess: boolean) => {
    if (!isAdmin) {
      enqueueSnackbar('You do not have permission to create a risk profile', {
        variant: 'error',
        persist: true,
      });
      return;
    }
    try {
      const contacts = riskData?.reviewDetails?.contacts || [];

      if (contacts.length === 0) {
        enqueueSnackbar('No contacts found to create risk profiles for', {
          variant: 'error',
          persist: true,
        });
        return;
      }

      // Create risk profile inputs for each contact with different scores and notes
      const riskProfileInputs = contacts.map((contact, index) => {
        const isFirstContact = index === 0;
        return {
          reviewId: reviewId!,
          contactId: contact.id,
          centralPropositionAgreed: true,
          dfmAgreed: true,
          hasNoRiskDicussion: false,
          isRiskToleranceQuestionnaireDeclined: false,
          riskToleranceQuestionnaireDeclineReason: null,
          noChangeReasonNotes: null,
          outcomeRiskScore: isFirstContact ? 3 : 5, // Different scores for first and second contact
          outcomeRiskNotes: isFirstContact
            ? `Dummy risk profile for ${
                contact.simplifiedName || 'Contact 1'
              } - Conservative approach with moderate risk tolerance.`
            : `Dummy risk profile for ${
                contact.simplifiedName || 'Contact 2'
              } - Aggressive approach with higher risk tolerance.`,
        };
      });

      const result = await upsertRiskProfile({
        variables: {
          inputs: riskProfileInputs,
        },
      });

      if (result.errors?.length) {
        enqueueSnackbar(
          `Error creating dummy risk profile: ${result.errors
            .map((e) => e.message)
            .join(', ')}`,
          {
            variant: 'error',
            persist: true,
          }
        );
        return;
      }
      if (result.data?.upsertRiskProfiles?.errors?.length) {
        enqueueSnackbar(
          `Error creating dummy risk profile: ${result.data.upsertRiskProfiles.errors.join(
            ', '
          )}`,
          {
            variant: 'error',
            persist: true,
          }
        );
        return;
      }

      // Now update risk sections for all contacts
      const allContacts = riskData?.reviewDetails?.contacts || [];
      const isJoint = allContacts.length > 1;
      const updatedSections: string[] = [];

      // Create all section inputs for all contacts in a single mutation
      const allSectionInputs = contacts.flatMap((contact, index) => {
        const capacityScore = isJoint ? (index === 0 ? 1 : 5) : 3;

        // Track what we're updating for the success message
        updatedSections.push(
          `${contact.simplifiedName || 'Contact'} BEHAVIOURAL_CAPACITY: 4`,
          `${contact.simplifiedName || 'Contact'} CAPACITY_FOR_LOSS: ${capacityScore}`,
          `${contact.simplifiedName || 'Contact'} KNOWLEDGE_AND_EXPERIENCE: no change`,
          `${contact.simplifiedName || 'Contact'} RISK_CAPACITY: no change`,
          `${contact.simplifiedName || 'Contact'} RISK_TOLERANCE: 1`,
          `${contact.simplifiedName || 'Contact'} TIMEFRAMES: 2`,
          `${contact.simplifiedName || 'Contact'} VALUES: dummy text`
        );

        return [
          // BEHAVIOURAL_CAPACITY: score 4 with dummy text
          {
            reviewId: reviewId!,
            contactId: contact.id,
            riskType: RiskType.BEHAVIOURAL_CAPACITY,
            score: 4,
            notes: 'Dummy behavioural capacity assessment for testing purposes.',
            noChange: false,
          },
          // CAPACITY_FOR_LOSS: different scores if joint
          {
            reviewId: reviewId!,
            contactId: contact.id,
            riskType: RiskType.CAPACITY_FOR_LOSS,
            score: capacityScore,
            notes: `Dummy capacity for loss assessment (${
              isJoint ? 'joint review' : 'single'
            }).`,
            noChange: false,
          },
          // KNOWLEDGE_AND_EXPERIENCE: no changes
          {
            reviewId: reviewId!,
            contactId: contact.id,
            riskType: RiskType.KNOWLEDGE_AND_EXPERIENCE,
            score: null,
            notes: null,
            noChange: true,
          },
          // RISK_CAPACITY: no changes
          {
            reviewId: reviewId!,
            contactId: contact.id,
            riskType: RiskType.RISK_CAPACITY,
            score: null,
            notes: null,
            noChange: true,
          },
          // RISK_TOLERANCE: score 1
          {
            reviewId: reviewId!,
            contactId: contact.id,
            riskType: RiskType.RISK_TOLERANCE,
            score: 1,
            notes: 'example text',
            noChange: false,
          },
          // TIMEFRAMES: score 2
          {
            reviewId: reviewId!,
            contactId: contact.id,
            riskType: RiskType.TIMEFRAMES,
            score: 2,
            notes: 'example text 2',
            noChange: false,
          },
          // VALUES: dummy text
          {
            reviewId: reviewId!,
            contactId: contact.id,
            riskType: RiskType.VALUES,
            score: null,
            notes:
              'Dummy values assessment - client values stability and long-term growth.',
            noChange: false,
          },
        ];
      });

      // Execute single mutation with all inputs
      await Promise.all([
        upsertRiskSection({
          variables: {
            inputs: allSectionInputs,
          },
          refetchQueries: [
            {
              query: REVIEW_VALIDATION,
              variables: {
                valueId: reviewId!,
              },
            },
            {
              query: GET_RISK,
              variables: {
                reviewId: reviewId!,
              },
            },
          ],
        }),
        ...contacts.map((contact) =>
          updateContactMutation({
            variables: {
              contactId: contact.id,
              giaHasLiquidityNeeds: true,
              pensionHasLiquidityNeeds: true,
            },
          })
        ),
      ]);

      if (showSuccess) {
        enqueueSnackbar(
          <Box>
            <Typography variant="subtitle1" fontWeight="bold" sx={{mb: 1}}>
              Dummy risk profiles created successfully for {contacts.length} contact(s)
            </Typography>
            {/*
          <Typography variant="subtitle2" fontWeight="bold" sx={{mb: 0.5}}>
            Risk Profile Values:
          </Typography>
          <List dense sx={{py: 0, mb: 1}}>
            {riskProfileInputs.map((input, index) => (
              <ListItem
                key={`profile-${contacts[index]?.id || index}`}
                sx={{py: 0.5, pl: 2}}
              >
                <ListItemText
                  primary={contacts[index]?.simplifiedName || `Contact ${index + 1}:`}
                  secondary={
                    <Box component="div" sx={{pl: 1, fontSize: '0.8rem'}}>
                      <Typography variant="body2" component="div">
                        • Central Proposition:{' '}
                        {input.centralPropositionAgreed ? 'Agreed' : 'Not Agreed'}
                      </Typography>
                      <Typography variant="body2" component="div">
                        • DFM Agreed: {input.dfmAgreed ? 'Yes' : 'No'}
                      </Typography>
                      <Typography variant="body2" component="div">
                        • Outcome Risk Score: {input.outcomeRiskScore}
                      </Typography>
                      <Typography variant="body2" component="div">
                        • Notes: {input.outcomeRiskNotes}
                      </Typography>
                    </Box>
                  }
                />
              </ListItem>
            ))}
          </List>

          <Typography variant="subtitle2" fontWeight="bold" sx={{mb: 0.5}}>
            Updated Sections:
          </Typography>
          <List dense sx={{py: 0}}>
            {updatedSections.map((section) => (
              <ListItem key={section} sx={{py: 0, pl: 2}}>
                <ListItemText
                  primary={section}
                  primaryTypographyProps={{variant: 'body2'}}
                />
              </ListItem>
            ))}
          </List>

          <Typography variant="subtitle2" fontWeight="bold" sx={{mb: 0.5, mt: 1}}>
            Liquidity Needs:
          </Typography>
          <List dense sx={{py: 0}}>
            {contacts.map((contact) => (
              <ListItem key={`liquidity-${contact.id}`} sx={{py: 0, pl: 2}}>
                <ListItemText
                  primary={`${
                    contact.simplifiedName || 'Contact'
                  }: GIA & Pension liquidity needs set to true`}
                  primaryTypographyProps={{variant: 'body2'}}
                />
              </ListItem>
            ))}
          </List> */}
          </Box>,
          {
            variant: 'success',
            autoHideDuration: 5000,
          }
        );
      }
    } catch (error) {
      enqueueSnackbar(
        `Error creating dummy risk profile: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`,
        {
          variant: 'error',
          autoHideDuration: 5000,
        }
      );
      throw error;
    }
  };

  return {
    upsertRiskProfile,
    upsertRiskProfileLoading,
    upsertRiskProfileError,
    upsertRiskSection,
    upsertRiskSectionLoading,
    upsertRiskSectionError,
    riskData,
    riskLoading,
    riskError,
    refetchRiskData,
    multiscoreEnabled,
    saveDummyRiskProfile,
    resyncCuroAtrForRiskProfileMutation,
    resyncCuroAtrForRiskProfileLoading,
    resyncCuroAtrForRiskProfileError,
  };
};

export const useRiskExternalOnly = useRiskProvider;

export const RiskProvider: React.FC<{children: React.ReactNode}> = ({children}) => {
  const contextValue = useRiskProvider();
  if (!contextValue.riskData) {
    return <Loading entityName="Risk" />;
  }
  return <RiskContext.Provider value={contextValue}>{children}</RiskContext.Provider>;
};

export const useRiskMutations = () => {
  const context = React.useContext(RiskContext);
  if (!context) {
    throw new Error('useRiskMutations must be used within a RiskProvider');
  }
  return context;
};
